/**
 * <LiveControlBar> — la barre du bas d'un live, côté élève (molecule).
 *
 * Les gestes d'aujourd'hui (`StudentControlBar`), dans leur ordre : le micro, la caméra, lever la
 * main, le chat (et ses messages non lus), « Un problème ? » (le point quand l'écran en a détecté
 * un), quitter. Sous 500 px de barre, « Un problème ? » passe dans le menu ⋮, comme aujourd'hui ;
 * c'est la largeur de la barre qui tranche, pas celle de la fenêtre. Avec l'apparence
 * (`appearance` : Automatique · Clair · Sombre, appliquée sans quitter le live), le menu ⋮ est
 * toujours là : au large, il suit « Un problème ? » et ne porte que l'apparence ; à l'étroit, il
 * porte les deux (`<LiveMoreSheet>`). Sous 344 px (un écran de
 * 320 px), les boutons passent à 44 px, la cible minimale, et se serrent : « Quitter » reste à
 * l'écran.
 *
 * Sur le web, un chevron vers le haut est accolé au micro et à la caméra (`onOpenMicDevices`,
 * `onOpenCameraDevices`), comme dans l'ancienne barre (`TrackToggleWithDeviceSelectM`) : il ouvre
 * « Choisis tes appareils », sur le micro ou sur la caméra. Avec eux, les boutons se serrent plus tôt
 * (sous 404 px de barre) pour que « Quitter » reste à l'écran.
 *
 * Sur le web, le partage de mon écran (`screenShare`) suit la caméra, comme dans la barre d'aujourd'hui
 * (`ControlBarM.web`) ; l'écran ne le passe que là où l'ancien le montrait. Partagé, il prend l'aplat
 * choisi de l'univers.
 *
 * Un micro ou une caméra que l'appareil (ou le navigateur) refuse portent un point (`micBlocked`,
 * `cameraBlocked`), comme le « ! » de l'ancienne barre : « Un problème ? » dira pourquoi.
 *
 * Chaque état se lit sur son bouton :
 *  - micro ou caméra coupés : la tinte d'erreur et le glyphe barré ;
 *  - en Concentré, le micro est violet et barré : c'est le tuteur qui l'a coupé. Il presse quand
 *    même (`onToggleMic`) : la page dit en une phrase pourquoi il ne s'ouvre pas. Au téléphone,
 *    c'est ainsi que le mode se lit, avec le liseré de la mini-visio du tuteur (Wajih, 17/10) ;
 *  - ma caméra allumée, quand la scène n'est pas à l'écran (`cameraPreview`) : le bouton EST ma
 *    vignette, un rond avec mon image ; coupée, le glyphe barré. Je me vois toujours. Quand elle
 *    arrive (l'élève passe de la visio à l'activité), elle glisse d'au-dessus de la barre et s'y
 *    pose (`<CameraArrival>`) ;
 *  - la main levée est jaune, l'aplat de la signature (`accent`, glyphe marine) : celui de la main
 *    sur la vignette (`<VideoTile>`), pour que l'élève et le tuteur la lisent pareil (Wajih, 20/10). Le
 *    bouton change au toucher, l'écran passe la valeur du geste sans attendre le serveur ;
 *  - quitter est l'aplat de ce qui détruit chez l'élève (`danger` → `danger-fill`, le rouge foncé).
 *
 * Elle ne dessine pas de surface : le gabarit qui la porte en pied (`<PageTemplate footer>`) pose
 * la sienne et son filet. Le menu ⋮ ouvert est un état d'interface, il vit ici.
 */
import React, { useEffect, useRef, useState } from "react"
import { Pressable, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Row } from "../../atoms/containers/Row"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { BadgedIconButton } from "../BadgedIconButton"
import { type UISoundMode } from "../SoundModeBadge"
import { type UILiveParticipant, VideoTile } from "../VideoTile"
import { CameraArrival } from "./CameraArrival"
import { LiveMicVoice } from "./LiveMicVoice"
import { LiveMoreSheet, type UILiveAppearance } from "./LiveMoreSheet"

export type { UILiveAppearance }

export type LiveControlBarProps = {
    /** Le mode que le tuteur a posé : en Concentré, le micro est le sien. */
    soundMode: UISoundMode
    micOn: boolean
    onToggleMic: () => void
    /** Ma vignette (son id) : ma voix au pied du bouton du micro, ouvert (`SpeakingContext`). Absente : rien. */
    voiceId?: string
    cameraOn: boolean
    onToggleCamera: () => void
    /** Le micro est refusé à l'app : un point sur son bouton. Défaut `false`. */
    micBlocked?: boolean
    /** La caméra est refusée à l'app : un point sur son bouton. Défaut `false`. */
    cameraBlocked?: boolean
    /**
     * Ma caméra, quand la scène n'est pas à l'écran (l'activité d'un téléphone) : allumée, le
     * bouton la montre en rond. Absente (défaut) : le glyphe de la caméra.
     */
    cameraPreview?: UILiveParticipant
    /** La vraie piste de ma caméra, dans ce rond (`VideoView` de LiveKit). Absente : ma photo. */
    cameraPreviewVideo?: React.ReactNode
    /** Le chevron accolé au micro : choisir son micro (le web). Absent (défaut) : pas de chevron. */
    onOpenMicDevices?: () => void
    /** Le chevron accolé à la caméra : choisir sa caméra (le web). Absent (défaut) : pas de chevron. */
    onOpenCameraDevices?: () => void
    /** Le partage de mon écran (le web seulement). Absent (défaut) : pas de bouton. */
    screenShare?: { on: boolean; pending?: boolean; onToggle: () => void }
    handRaised: boolean
    onToggleHand: () => void
    /** Les messages non lus du chat. Défaut 0. */
    unreadCount?: number
    onOpenChat: () => void
    /** L'écran a détecté un problème (micro muet, connexion) : un point sur « Un problème ? ». */
    problemDetected?: boolean
    onOpenHelp: () => void
    /**
     * L'apparence du live, dans le menu ⋮ : la même préférence que celle de « Mon compte ». Absente
     * (défaut) : le menu ⋮ ne paraît qu'à l'étroit, pour « Un problème ? ».
     */
    appearance?: UILiveAppearance
    onLeave: () => void
    /** La sortie part : quitter tourne et ne répond plus. Défaut `false`. */
    leaving?: boolean
    testID?: string
}

/** Sous cette largeur de barre, « Un problème ? » passe dans le menu, comme aujourd'hui. */
const CONTROL_BAR_MENU_BELOW = 500
/** Une cible de doigt, un peu plus que le minimum : ce sont les gestes du live. */
export const LIVE_CONTROL_SIZE = 48
/**
 * Sous cette largeur de barre (six boutons de 48, cinq écarts de 8 et le rembourrage de la
 * rangée), « Quitter » déborderait : sur un écran de 320 px, les boutons passent à la cible
 * minimale et se serrent.
 */
const CONTROL_BAR_TIGHT_BELOW = 344
/** Le chevron d'un appareil : étroit, accolé à son bouton. */
const CHEVRON_SIZE = 26
/** Les deux chevrons et leurs écarts : les boutons se serrent d'autant plus tôt. */
const CHEVRONS_ROOM = 60
const TIGHT_CONTROL_SIZE = 44
export const LIVE_CONTROL_ICON_SIZE = 22
const ICON_SIZE = LIVE_CONTROL_ICON_SIZE

/** Les mots du micro et de la caméra, les mêmes avant le live (`<LiveLobbyPreview>`) et pendant. */
export const LIVE_DEVICE_LABELS = {
    micOn: "Couper mon micro",
    micOff: "Ouvrir mon micro",
    cameraOn: "Couper ma caméra",
    cameraOff: "Ouvrir ma caméra",
    /** Le point d'un appareil que l'élève ne peut pas utiliser : il dit où chercher. */
    blocked: "bloqué sur cet appareil",
}

const TEXTS = {
    ...LIVE_DEVICE_LABELS,
    micFocused: "Micro coupé par ton masteur",
    micDevices: "Choisir mon micro",
    cameraDevices: "Choisir ma caméra",
    shareScreen: "Partager mon écran",
    stopShare: "Arrêter le partage",
    raiseHand: "Lever la main",
    lowerHand: "Baisser la main",
    chat: "Chat",
    unread: (count: number) => (count === 1 ? "1 message non lu" : `${count} messages non lus`),
    problem: "un problème a été détecté",
    help: "Un problème ?",
    more: "Plus d'actions",
    leave: "Quitter le live",
}

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

export type LiveControlFace = { color: SemanticColorKey; iconColor: SemanticColorKey }
type Face = LiveControlFace

/** Un bouton au repos : la surface douce de la page, le glyphe au marine du socle. */
const REST: Face = { color: "surface-subtle", iconColor: "primary" }
/** Un micro ou une caméra coupés : la tinte d'erreur. */
const MUTED: Face = { color: "dangerLight", iconColor: "danger-strong" }
/** Un micro ou une caméra, ouverts ou coupés : la même face avant le live et pendant. */
export const LIVE_DEVICE_FACES = { on: REST, off: MUTED } as const
/** Le micro que le tuteur a coupé : le violet du live. */
const FOCUSED: Face = { color: "purple", iconColor: "white" }
/** Mon écran partagé : l'aplat choisi de l'univers, comme une option choisie. */
const CHOSEN: Face = { color: "role-selected", iconColor: "text-on-role" }
/** La main levée : le jaune de la signature, comme sa marque sur la vignette (`<VideoTile>`). */
const RAISED: Face = { color: "accent", iconColor: "text-on-accent" }

export function LiveControlBar(props: LiveControlBarProps) {
    const {
        soundMode,
        micOn,
        onToggleMic,
        voiceId,
        cameraOn,
        onToggleCamera,
        micBlocked = false,
        cameraBlocked = false,
        cameraPreview,
        cameraPreviewVideo,
        screenShare,
        onOpenMicDevices,
        onOpenCameraDevices,
        handRaised,
        onToggleHand,
        unreadCount = 0,
        onOpenChat,
        problemDetected = false,
        onOpenHelp,
        appearance,
        onLeave,
        leaving = false,
        testID,
    } = props
    const menu = useWidthAtLeast(CONTROL_BAR_MENU_BELOW)
    const chevrons = onOpenMicDevices != null || onOpenCameraDevices != null
    const fits = useWidthAtLeast(CONTROL_BAR_TIGHT_BELOW + (chevrons ? CHEVRONS_ROOM : 0))
    const onLayout = (event: LayoutChangeEvent) => {
        menu.onLayout(event)
        fits.onLayout(event)
    }
    const roomy = menu.atLeast
    const size = fits.atLeast ? LIVE_CONTROL_SIZE : TIGHT_CONTROL_SIZE
    const [menuOpen, setMenuOpen] = useState(false)
    // Ma vignette ne glisse qu'à un passage de vue : présente dès l'ouverture du live, elle est posée.
    const mountedRef = useRef(false)
    useEffect(() => {
        mountedRef.current = true
    }, [])
    // Elle ne glisse qu'à l'arrivée de la vue qui la porte (`cameraPreview` qui paraît), jamais quand ma
    // caméra se rallume sur la même vue : le rond se remonte alors, et glissait à chaque fois.
    const previewShown = cameraPreview != null
    const previewBeforeRef = useRef(previewShown)
    const previewArrives = mountedRef.current && !previewBeforeRef.current
    useEffect(() => {
        previewBeforeRef.current = previewShown
    }, [previewShown])

    const focused = soundMode === "focused"
    const mic: Face = focused ? FOCUSED : micOn ? REST : MUTED
    const camera: Face = cameraOn ? REST : MUTED
    const hand: Face = handRaised ? RAISED : REST

    // Le bouton et son chevron se tiennent, sans écart : un seul geste, deux cibles.
    const withChevron = (button: React.ReactNode, onOpen: (() => void) | undefined, label: string) =>
        onOpen == null ? (
            button
        ) : (
            <Row gap="0" align="center">
                {button}
                <IconButton
                    iconName="chevron-up"
                    appearance="transparent"
                    iconColor="primary"
                    size={CHEVRON_SIZE}
                    iconSize={14}
                    onPress={onOpen}
                    accessibilityLabel={label}
                />
            </Row>
        )

    return (
        <View onLayout={onLayout} testID={testID}>
            <Row gap={roomy ? "3" : fits.atLeast ? "2" : "1"} align="center" justify="center" style={styles.row}>
                {withChevron(
                    <LiveMicVoice voiceId={voiceId} on={micOn && !focused}>
                        <BadgedIconButton
                            iconName={focused || !micOn ? "microphone-slash" : "microphone"}
                            {...mic}
                            size={size}
                            iconSize={ICON_SIZE}
                            dot={micBlocked}
                            dotLabel={TEXTS.blocked}
                            onPress={onToggleMic}
                            accessibilityLabel={focused ? TEXTS.micFocused : micOn ? TEXTS.micOn : TEXTS.micOff}
                        />
                    </LiveMicVoice>,
                    onOpenMicDevices,
                    TEXTS.micDevices
                )}
                {withChevron(
                    cameraOn && cameraPreview != null ? (
                        <CameraArrival animate={previewArrives}>
                            <Pressable
                                onPress={onToggleCamera}
                                accessibilityRole="button"
                                accessibilityLabel={TEXTS.cameraOn}
                                style={({ pressed }: PressableVisualState) => [
                                    focusRingHost,
                                    pressed && styles.pressed,
                                ]}
                            >
                                {({ focused }: PressableVisualState) => (
                                    <>
                                        <FocusRing visible={focused === true} radius={size / 2} />
                                        <VideoTile
                                            participant={cameraPreview}
                                            size="round"
                                            diameter={size}
                                            me
                                            video={cameraPreviewVideo}
                                        />
                                    </>
                                )}
                            </Pressable>
                        </CameraArrival>
                    ) : (
                        <BadgedIconButton
                            iconName={cameraOn ? "video" : "video-slash"}
                            {...camera}
                            size={size}
                            iconSize={ICON_SIZE}
                            dot={cameraBlocked}
                            dotLabel={TEXTS.blocked}
                            onPress={onToggleCamera}
                            accessibilityLabel={cameraOn ? TEXTS.cameraOn : TEXTS.cameraOff}
                        />
                    ),
                    onOpenCameraDevices,
                    TEXTS.cameraDevices
                )}
                {screenShare != null && (
                    <IconButton
                        iconName="desktop"
                        {...(screenShare.on ? CHOSEN : REST)}
                        size={size}
                        iconSize={ICON_SIZE}
                        onPress={screenShare.onToggle}
                        loading={screenShare.pending === true}
                        accessibilityLabel={screenShare.on ? TEXTS.stopShare : TEXTS.shareScreen}
                    />
                )}
                <View>
                    <IconButton
                        iconName="hand-paper"
                        {...hand}
                        size={size}
                        iconSize={ICON_SIZE}
                        onPress={onToggleHand}
                        accessibilityLabel={handRaised ? TEXTS.lowerHand : TEXTS.raiseHand}
                        selected={handRaised}
                    />
                    {/* En clair, le jaune se lit à peine sur la barre blanche (1,2:1) : un contour dit
                        l'état levé sans dépendre de la couleur. Sur la nuit, le jeton est transparent. */}
                    {handRaised && <View pointerEvents="none" style={styles.raisedRing(size)} />}
                </View>
                <BadgedIconButton
                    iconName="comment-dots"
                    {...REST}
                    size={size}
                    iconSize={ICON_SIZE}
                    count={unreadCount}
                    countLabel={TEXTS.unread}
                    onPress={onOpenChat}
                    accessibilityLabel={TEXTS.chat}
                />
                <BadgedIconButton
                    iconName={roomy ? "stethoscope" : "ellipsis-h"}
                    {...REST}
                    size={size}
                    iconSize={ICON_SIZE}
                    dot={problemDetected}
                    dotLabel={TEXTS.problem}
                    onPress={roomy ? onOpenHelp : () => setMenuOpen(true)}
                    accessibilityLabel={roomy ? TEXTS.help : TEXTS.more}
                />
                {roomy && appearance != null && (
                    <IconButton
                        iconName="ellipsis-h"
                        {...REST}
                        size={size}
                        iconSize={ICON_SIZE}
                        onPress={() => setMenuOpen(true)}
                        accessibilityLabel={TEXTS.more}
                    />
                )}
                <IconButton
                    iconName="phone-slash"
                    color="danger"
                    iconColor="white"
                    size={size}
                    iconSize={ICON_SIZE}
                    onPress={onLeave}
                    loading={leaving}
                    accessibilityLabel={TEXTS.leave}
                />
            </Row>

            <LiveMoreSheet
                visible={menuOpen}
                onClose={() => setMenuOpen(false)}
                onOpenHelp={roomy ? undefined : onOpenHelp}
                helpLabel={TEXTS.help}
                appearance={appearance}
            />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le compte du chat déborde en haut de son bouton : la rangée lui laisse sa place. */
    row: {
        paddingTop: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
    },
    /** Le retour de pression de ma vignette : celui d'un bouton d'icône (`<IconButton>`). */
    pressed: { transform: [{ scale: 0.98 }] },
    /** Le contour de la main levée en clair, posé dans le rond du bouton : la rangée ne bouge pas. */
    raisedRing: (size: number) => ({
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: 2,
        borderColor: theme.colors["raised-ring"],
    }),
}))
